// @ts-nocheck
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import getColor from "@insite/mobius/utilities/getColor";
import getHasAccessoriesProperty from "@store/Framework/Common/Utilities/getHasAccessoriesProperty";
import { getProductLineProductId } from "@store/Framework/Common/Utilities/productLineUtilities";
import ProductAccessoryUpsell, {
    ProductAccessoryUpsellPresentationProps,
    ProductAccessoryUpsellStyles,
} from "@store/Library/Components/ProductAccessoryUpsell";
import { ProductLineComponentModel, ProductLineProps } from "@store/Library/Components/ProductLine/ProductLine";
import { productLineV2Props } from "@store/Themes/common/props/productLineV2";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, HTMLAttributes, useMemo } from "react";
import { css } from "styled-components";

export interface ProductLineAccessoryUpsellProps extends HTMLAttributes<HTMLElement> {
    gap?: ProductLineProps["gap"];
    productInfo?: ProductInfo;
    productLine?: ProductLineComponentModel;
}

export interface ProductLineAccessoryUpsellStyles {
    productAccessoryUpsell?: ProductAccessoryUpsellPresentationProps;
}

export const productLineAccessoryUpsellStyles: ProductLineAccessoryUpsellStyles = {
    productAccessoryUpsell: {
        extendedStyles: {
            productWrapper: {
                css: css`
                    gap: ${pxToRem(13, 16)};
                    padding: ${pxToRem(16, 0, 0)};
                    margin-top: ${pxToRem(16)};
                    border: 0;
                    border-top: 1px solid ${getColor("common.borderLight")};
                    border-radius: 0;

                    &:first-child {
                        padding: 0;
                        margin: 0;
                        border: 0;
                    }
                `,
            },
            imageWrapper: {
                css: css`
                    align-self: flex-start;
                `,
            },
            productPartNumbers: {
                wrapper: {
                    css: css`
                        margin-bottom: ${pxToRem(4)};
                        font-size: ${x => x.theme.design.typography.size.smaller};

                        ${breakpoint("sm")} {
                            margin-bottom: ${pxToRem(5)};
                        }
                    `,
                },
            },
            productTitle: {
                css: css`
                    margin-bottom: ${pxToRem(5)};

                    ${breakpoint("sm")} {
                        margin-bottom: ${pxToRem(6)};
                        font-size: ${x => x.theme.design.typography.size.large};
                    }
                `,
            },
            infoWrapper: {
                css: css`
                    ${breakpoint("sm")} {
                        font-size: ${x => x.theme.design.typography.size.base};
                    }
                `,
            },
        },
        overridedStyles: {
            wrapper: mergeProps<ProductAccessoryUpsellStyles["wrapper"]>(productLineV2Props.wrapper, {
                css: css`
                    padding: ${pxToRem(16)};

                    ${breakpoint("sm")} {
                        padding: ${pxToRem(16, 20)};
                    }

                    ${breakpoint("md")} {
                        padding: ${pxToRem(16, 24)};
                    }
                `,
            }),
            productListWrapper: null as any,
            image: {
                css: css`
                    display: block;
                    width: ${pxToRem(80)};
                    max-width: 100%;
                    height: auto;
                    object-fit: contain;
                `,
            },
        },
    },
};

const ProductLineAccessoryUpsell: FC<ProductLineAccessoryUpsellProps> = ({
    gap,
    productInfo,
    productLine,
    ...htmlAttributes
}) => {
    const hasAccessories = getHasAccessoriesProperty(productLine);
    const productId = getProductLineProductId(productLine, productInfo);
    const warehouse = (productInfo || productLine)?.pricing?.additionalResults?.warehouseName;
    const styles = useMemo(() => {
        return mergeProps<ProductLineAccessoryUpsellStyles>(productLineAccessoryUpsellStyles, {
            productAccessoryUpsell: {
                overridedStyles: {
                    wrapper: {
                        css: css`
                            ${productLineV2Props.getWrapperGapStyles(gap)}
                        `,
                    },
                },
            },
        });
    }, [gap]);

    if (!hasAccessories || !productId || !warehouse) {
        return null;
    }

    return (
        <ProductAccessoryUpsell
            {...styles.productAccessoryUpsell}
            {...htmlAttributes}
            itemCount={productLine?.qtyOrdered}
            refId={productLine?.properties.refId}
            productId={productId}
            showAddToCart
            warehouse={warehouse}
        />
    );
};

export default ProductLineAccessoryUpsell;
